// Draft viewer — narrative + statutory anchor + Mode A defects +
// sources block + DOCX/PDF export.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, drafts } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "../components/Toast";

export function DraftDetailPage(): JSX.Element {
  const params = useParams<{ draftId: string }>();
  const draftId = Number(params.draftId);
  const { data, loading, error } = useAsync(() => drafts.get(draftId), [draftId]);
  const [exporting, setExporting] = useState<"docx" | "pdf" | null>(null);

  if (Number.isNaN(draftId)) return <p className="text-sm text-red-700">Invalid draft id.</p>;
  if (loading) return <p className="text-sm text-slate-500">Loading draft…</p>;
  if (error) return <p className="text-sm text-red-700">{error.message}</p>;
  if (!data) return <p className="text-sm text-slate-500">No draft.</p>;

  const onExport = async (fmt: "docx" | "pdf"): Promise<void> => {
    setExporting(fmt);
    try {
      const blob = fmt === "docx" ? await drafts.exportDocx(draftId) : await drafts.exportPdf(draftId);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `dwc154-draft-${draftId}.${fmt}`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      pushToast("success", `Exported draft as ${fmt.toUpperCase()}`);
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : `Export failed (${fmt})`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to={`/cases/${data.case_id}`} className="hover:text-brand-700">
          Case #{data.case_id}
        </Link>
        <span aria-hidden> / </span>
        <span>Draft #{draftId}</span>
      </nav>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            DWC-154 draft #{draftId}
          </h1>
          <p className="text-sm text-slate-500">
            target: {data.target_type.replace(/_/g, " ")} ·{" "}
            citation status:{" "}
            {data.citation_status === "verified" ? (
              <span className="badge-green">verified</span>
            ) : (
              <span className="badge-red">{data.citation_status}</span>
            )}{" "}
            · generated{" "}
            {new Date(data.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={exporting !== null}
            onClick={() => onExport("docx")}
          >
            {exporting === "docx" ? "Exporting…" : "Export DOCX"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={exporting !== null}
            onClick={() => onExport("pdf")}
          >
            {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </button>
        </div>
      </div>

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Alleged conduct</h2>
        <p className="text-sm text-slate-700">{data.alleged_conduct}</p>
      </section>

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Statutory anchor</h2>
        <div className="text-sm">
          <div>
            <span className="font-medium text-slate-900">{data.statutory_anchor.citation}</span>
            {" — "}
            <span className="text-slate-700">{data.statutory_anchor.title}</span>
          </div>
          {data.statutory_anchor.summary && (
            <p className="mt-1 max-w-prose-narrow text-slate-600">
              {data.statutory_anchor.summary}
            </p>
          )}
        </div>
      </section>

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Narrative</h2>
        <pre className="max-w-prose-narrow whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
          {data.narrative}
        </pre>
      </section>

      {data.mode_a_defects.length > 0 && (
        <section className="card mb-4 p-5">
          <h2 className="mb-2 text-lg font-medium text-slate-800">
            Defects ({data.mode_a_defects.length})
          </h2>
          <ul role="list" className="space-y-3">
            {data.mode_a_defects.map((d, i) => (
              <li key={i} className="border-l-2 border-brand-300 pl-3 text-sm">
                <div className="font-medium text-slate-800">
                  {d.kind.replace(/_/g, " ")}
                </div>
                <p className="mt-1 max-w-prose-narrow text-slate-700">{d.summary}</p>
                <p className="mt-1 text-xs text-slate-500">
                  source doc #{d.source_document_id}
                  {d.supporting_document_ids.length > 0 && (
                    <> · supporting docs: {d.supporting_document_ids.map((x) => `#${x}`).join(", ")}</>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card mb-4 p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Requested relief</h2>
        <p className="max-w-prose-narrow text-sm text-slate-700">{data.requested_relief}</p>
      </section>

      <section className="card p-5">
        <h2 className="mb-2 text-lg font-medium text-slate-800">Sources</h2>
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Documents
            </h3>
            <ul role="list" className="space-y-1">
              {data.sources.documents.map((s) => (
                <li key={s.document_id}>
                  <Link
                    to={`/documents/${s.document_id}`}
                    className="text-brand-700 hover:underline"
                  >
                    #{s.document_id} {s.filename}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Statutes
            </h3>
            <ul role="list" className="space-y-1">
              {data.sources.statutes.map((s, i) => (
                <li key={i}>
                  <span className="font-medium">{s.citation}</span> — {s.title}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Defects
            </h3>
            <ul role="list" className="space-y-1">
              {data.sources.defects.map((s, i) => (
                <li key={i}>{s.kind.replace(/_/g, " ")} (doc #{s.source_document_id})</li>
              ))}
              {data.sources.defects.length === 0 && (
                <li className="text-slate-500">No defects in this draft.</li>
              )}
            </ul>
          </div>
        </div>
      </section>

      <CorpusVersionFooter
        corpusVersion={data.corpus_version}
        corpusAppliedAt={data.corpus_applied_at}
        isStale={data.is_stale}
      />
    </div>
  );
}

function CorpusVersionFooter({
  corpusVersion,
  corpusAppliedAt,
  isStale,
}: {
  corpusVersion: string | null | undefined;
  corpusAppliedAt: string | null | undefined;
  isStale: boolean | undefined;
}): JSX.Element {
  if (!corpusVersion) {
    return (
      <footer className="mt-6 text-xs text-slate-500">
        Citations verified against corpus: (pre-Phase-9 draft, no corpus version pinned).
      </footer>
    );
  }
  let days: number | null = null;
  if (corpusAppliedAt) {
    days = Math.floor(
      (Date.now() - new Date(corpusAppliedAt).getTime()) / (1000 * 60 * 60 * 24),
    );
  }
  return (
    <footer
      className={`mt-6 text-xs ${
        isStale ? "text-amber-700" : "text-slate-500"
      }`}
    >
      Citations verified against corpus <span className="font-mono">{corpusVersion}</span>
      {days !== null && (
        <>
          {" "}
          (refreshed {days} day{days === 1 ? "" : "s"} ago)
        </>
      )}
      {isStale && (
        <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
          Stale — over 180 days old
        </span>
      )}
    </footer>
  );
}
